
export function cleanAuthorName(name?: string): string {
  if (!name) return 'Anonymous Author';
  let cleaned = name.replace(/\|?\s*@[\w_.-]+/g, '').replace(/^@/, '').trim();
  return cleaned || 'Anonymous Author';
}

export function isGeneratedAuthorName(name?: string | null): boolean {
  return Boolean(name && /^User_[\w-]+$/i.test(name.trim()));
}

export function getPublicAuthorName(item?: {
  author_name?: string | null;
  display_name?: string | null;
  full_name?: string | null;
  profiles?: {
    display_name?: string | null;
    full_name?: string | null;
  } | null;
} | null): string {
  if (!item) return 'Anonymous Author';
  const profile = item.profiles;
  const candidates = [
    item.display_name || profile?.display_name,
    item.full_name || profile?.full_name,
    item.author_name,
  ];
  const publicName = candidates.find((name) => name?.trim() && !isGeneratedAuthorName(name));
  return publicName ? cleanAuthorName(publicName) : 'Anonymous Author';
}

export function getAuthorDisplayName(item?: {
  author_name?: string | null;
  display_name?: string | null;
  full_name?: string | null;
  name_display_preference?: 'display_name' | 'full_name' | string | null;
  profiles?: {
    display_name?: string | null;
    full_name?: string | null;
    name_display_preference?: 'display_name' | 'full_name' | string | null;
  } | null;
} | null): string {
  if (!item) return 'Anonymous Author';
  const nestedProfile = item.profiles;
  const pref = item.name_display_preference || nestedProfile?.name_display_preference;
  const fullName = item.full_name || nestedProfile?.full_name;
  const displayName = item.display_name || nestedProfile?.display_name;

  if (pref === 'full_name' && fullName && fullName.trim() && !isGeneratedAuthorName(fullName)) {
    return cleanAuthorName(fullName);
  }
  if (displayName && displayName.trim() && !isGeneratedAuthorName(displayName)) {
    return cleanAuthorName(displayName);
  }
  if (fullName && fullName.trim() && !isGeneratedAuthorName(fullName)) {
    return cleanAuthorName(fullName);
  }
  if (item.author_name && item.author_name.trim() && !isGeneratedAuthorName(item.author_name)) {
    return cleanAuthorName(item.author_name);
  }
  return getPublicAuthorName(item);
}

export type ThemePreference = 'light' | 'dark';

export interface BankDetails {
  method: 'bank_transfer' | 'paypal';
  bank_name?: string;
  account_number?: string;
  account_name?: string;
  paypal_email?: string;
}

export interface PayoutTransaction {
  id: string;
  user_id?: string;
  user_name?: string;
  amount: number;
  date: string;
  status: 'processed' | 'pending' | 'failed' | 'rejected';
  method: string;
  reference: string;
  bank_details_snapshot?: string;
}

export interface Notification {
  id: string;
  user_id: string;
  actor_id: string;
  actor_name: string;
  actor_avatar: string;
  type: 'like' | 'comment' | 'follow' | 'tip' | 'competition_win';
  resource_id?: string;
  resource_type?: 'poem' | 'article' | 'essay' | 'book' | 'chapter';
  book_id?: string; 
  chapter_index?: number;
  preview_text?: string;
  is_read: boolean;
  created_at: string;
}

export interface Profile {
  id: string;
  email: string;
  full_name: string;
  display_name: string;
  bio: string;
  avatar_url: string;
  is_active: boolean;
  is_admin?: boolean;
  date_of_birth?: string;
  updated_at?: string;
  joined_date?: string;
  theme_preference: ThemePreference;
  followers_count: number;
  following_count: number;
  total_views: number;
  book_reads: number;
  poem_views: number;
  total_likes: number;
  monthly_read_time?: number; // In minutes
  reading_streak?: number;
  longest_streak?: number;
  last_read_at?: string;
  wallet_balance: number;
  ink_drop_balance: number;
  hide_wallet_balance?: boolean;
  show_nsfw?: boolean;
  is_adult?: boolean;
  is_verified?: boolean;
  bank_details?: BankDetails;
  bank_details_snapshot?: string;
  last_payout_date?: string;
  payout_history?: PayoutTransaction[];
  has_selected_pen_name?: boolean;
  pen_name_updated_at?: string;
  pen_name_history?: string[];
  name_display_preference?: 'display_name' | 'full_name';
  total_earnings?: number;
  is_flagged?: boolean;
  fraud_score?: number;
  audit_logs?: any[];
  last_audit_date?: string;
}

export interface Book {
  id: string;
  author_id: string;
  title: string;
  summary: string;
  genre: string;
  themes?: string[]; // AI-detected themes
  mood?: string;     // AI-detected mood
  cover_url: string;
  is_published: boolean;
  is_nsfw?: boolean;
    is_ai_generated?: boolean;
  total_reads: number;
  likes_count?: number;
  comments_count?: number;
  ink_count?: number; // Total ink received
  monthly_read_time?: number; // In minutes
  created_at: string;
  author_name?: string;
  series_title?: string;
  series_order?: number;
  lit_isbn: string;
  isbn?: string; 
  status?: 'completed' | 'ongoing';
}

export type BlockType = 'text' | 'poll' | 'quiz' | 'choice';

export interface TextBlock {
  id: string;
  type: 'text';
  content: string;
}

export interface PollOption {
  id: string;
  text: string;
  votes: number;
}

export interface PollBlock {
  id: string;
  type: 'poll';
  question: string;
  options: PollOption[];
}

export interface QuizQuestion {
    id: string;
    text: string;
    options: { id: string; text: string; }[];
    correctOptionId: string;
}

export interface QuizBlock {
    id: string;
    type: 'quiz';
    questions: QuizQuestion[];
}

export interface Choice {
    id: string;
    text: string;
    nextChapterIndex: number;
}

export interface ChoiceBlock {
    id: string;
    type: 'choice';
    prompt: string;
    choices: Choice[];
}

export type ContentBlock = TextBlock | PollBlock | QuizBlock | ChoiceBlock;

export interface Chapter {
  id: string;
  book_id: string;
  title: string;
  content: ContentBlock[];
  order_index: number;
  likes_count?: number;
  comments_count?: number;
  created_at: string;
  is_published: boolean;
}

export interface Post {
  id: string;
  user_id: string;
  title?: string; 
  theme?: string;
  niche?: string;
  themes?: string[]; // AI-detected themes
  content: string;
  media_url?: string;
  background_url?: string;
  created_at: string;
  author_name: string;
  author_avatar: string;
  likes_count: number;
  views_count?: number;
  comments_count?: number;
  status: 'published' | 'draft';
  is_competition_entry?: boolean;
  is_nsfw?: boolean;
    is_ai_generated?: boolean;
  ink_drop_votes?: number;
  monthly_read_time?: number; // In minutes
  type?: 'poem' | 'article' | 'essay';
  hashtag?: string;
}

export interface Comment {
  id: string;
  post_id?: string;
  book_id?: string;
  chapter_id?: string;
  parent_id?: string;
  user_id: string;
  user_name: string;
  user_avatar: string;
  content: string;
  rating?: number;
  created_at: string;
  replies?: Comment[];
}

export interface Follow {
  follower_id: string;
  following_id: string;
}

export interface CompetitionStage {
    id: string;
    name: string;
    prompt?: string;
    description?: string;
    status: 'upcoming' | 'active' | 'completed';
    start_date?: string;
    end_date?: string;
    advancement_criteria?: string;
    advanced_entry_ids?: string[];
}

export interface CompetitionPrize {
    place: string;
    reward: string;
    description?: string;
}

export interface Competition {
    id: string;
    title: string;
    description: string;
    prompt?: string;
    hashtag: string;
    entry_fee: number;
    is_free?: boolean;
    prize_pool: number;
    prizes?: CompetitionPrize[];
    rules?: string;
    requirements?: string;
    start_date: string;
    end_date: string;
    close_date?: string;
    announcement_date?: string;
    created_at?: string;
    is_active?: boolean;
    status?: 'active' | 'upcoming' | 'ended' | 'completed' | 'closed' | 'archived' | 'deleted';
    type: 'poem' | 'short_story' | 'novel_chapter' | 'book' | 'essay';
    voting_type?: 'ink' | 'likes';
    allow_ink_voting?: boolean;
    entries_count?: number;
    stages?: CompetitionStage[];
    current_stage_id?: string;
    current_stage_index?: number;
    is_single_round?: boolean;
}

export interface CompetitionEntry {
    id: string;
    competition_id: string;
    user_id: string;
    post_id?: string;
    post_type?: string;
    book_id?: string;
    entry_date: string;
    ink_drop_votes: number;
    post_title: string;
    post_content_snippet: string;
    post_background_url?: string;
    author_name: string;
    author_avatar: string;
    likes_count?: number;
    stage_status?: 'submitted' | 'shortlisted' | 'advanced' | 'finalist' | 'winner' | 'eliminated';
    current_stage_id?: string;
    stage_name?: string;
}

export interface Community {
  id: string;
  creator_id: string;
  name: string;
  description: string;
  guidelines?: string;
  image_url: string;
  is_private: boolean;
  members_count: number;
  created_at: string;
}

export interface CommunityMember {
  id: string;
  community_id: string;
  user_id: string;
  role: 'creator' | 'admin' | 'member';
  created_at: string;
}

export interface CommunityPost {
  id: string;
  community_id: string;
  user_id: string;
  title?: string;
  content: string;
  created_at: string;
  author_name?: string;
  author_avatar?: string;
}

// --- ADMIN PLATFORM OVERVIEW INTERFACES ---

export interface UserAdActivityRecord {
  id?: string;
  user_id: string;
  date: string;
  sessions: number;
  impressions: number;
  page_views: number;
  last_active: string;
}

export interface AdminOverviewResponse {
  generatedAt: string;
  range: { startDate: string; endDate: string };
  totals: {
    users: number;
    newUsers7d: number;
    posts: number;
    books: number;
    pendingPayoutCount: number;
    pendingPayoutAmount: number;
    paidOutAmount: number;
    walletLiability: number;
    activeCompetitions: number;
  };
  recentContent: Array<{
    id: string;
    title: string;
    type: string;
    author_name?: string;
    created_at: string;
    path: string;
  }>;
  topEarners: Array<{
    id: string;
    display_name?: string;
    email?: string;
    wallet_balance?: number;
  }>;
  activity: {
    impressions: number;
    pageViews: number;
    clicks: number;
    sessions: number;
    activeUsers: number;
    available: boolean;
  };
  integrations: {
    ai: { configured: boolean; provider: string; detail: string };
    indexing: { configured: boolean; source: string; email: string | null };
    ads: { provider: string; externalApi: boolean; detail: string };
  };
  degraded?: boolean;
}
